import {createServerFn} from '@tanstack/react-start';
import {z} from 'zod';
import {optionalAuthMiddleware} from '@/helpers/authMiddleware';
import {prismaClient} from '@/helpers/prismaClient.server';
import type {
  ProfileMembership,
  PublicProfileListsPage,
  PublicProfileWithLists,
} from '@/apiTypes/profile';
import type {EventListSummary} from '@/apiTypes/eventList';
import {Event} from '@/apiTypes/event';

type EventListWithCounts = {
  id: string;
  name: string;
  description: string | null;
  coverImageUrl: string | null;
  visibility: string;
  createdAt: Date;
  _count: {items: number; followers: number; subscriptions: number};
};

function toEventListSummary(list: EventListWithCounts): EventListSummary {
  return {
    id: list.id,
    name: list.name,
    description: list.description,
    coverImageUrl: list.coverImageUrl,
    itemCount: list._count.items,
    visibility: list.visibility === 'PUBLIC' ? 'PUBLIC' : 'PRIVATE',
    createdAt: list.createdAt,
    followerCount: list._count.followers + list._count.subscriptions,
  };
}

const PUBLIC_LIST_PREVIEW_COUNT = 4;

/** Prisma where filter for non-archived events that haven't ended yet. */
function futureEventFilter(now: Date) {
  return {
    archived: false,
    OR: [
      {endDate: {gte: now}},
      {endDate: null, startDate: {gte: now}},
      {endDate: null, startDate: null},
    ],
  };
}

/** Lightweight include matching the shape MobileEventDetailCard reads. */
const eventCardInclude = {
  routes: {
    select: {
      id: true,
      createdAt: true,
      updatedAt: true,
      name: true,
      description: true,
      distance_meters: true,
      elevation_gain_meters: true,
      routeFileUrl: true,
      eventId: true,
    },
  },
  categories: {include: {prices: true}},
} as const;

const GetPublicProfileInput = z.object({
  profileId: z.string().uuid(),
});

/**
 * Returns a public profile with up to PUBLIC_LIST_PREVIEW_COUNT of the
 * user's PUBLIC event lists (most recent first), plus the total public
 * list count for a future "see all" affordance.
 *
 * Returns null when the profile id does not match any user — the route's
 * errorComponent renders that as a not-found state.
 */
export const getPublicProfile = createServerFn({method: 'GET'})
  .middleware([optionalAuthMiddleware])
  .inputValidator((data: unknown) => {
    const result = GetPublicProfileInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(
    async ({data, context}): Promise<PublicProfileWithLists | null> => {
      const db = await prismaClient();
      const viewerProfileId = context.auth?.profile.id ?? null;

      const profileRow = await db.profile.findUnique({
        where: {id: data.profileId},
        select: {
          id: true,
          displayName: true,
          firstName: true,
          lastName: true,
          avatarUrl: true,
          memberOf: {
            where: {leftAt: null},
            orderBy: {createdAt: 'desc'},
            select: {
              createdAt: true,
              organizer: {
                select: {id: true, name: true, logoUrl: true},
              },
            },
          },
        },
      });

      if (!profileRow) return null;

      const {memberOf, ...profile} = profileRow;

      const memberships: ProfileMembership[] = memberOf.map((m) => ({
        id: m.organizer.id,
        name: m.organizer.name,
        logoUrl: m.organizer.logoUrl,
        memberSince: m.createdAt,
      }));

      const publicListsWhere = {
        profileId: profile.id,
        visibility: 'PUBLIC',
      };

      const [publicListsCount, listRows, followerCount, followingCount] =
        await db.$transaction([
          db.eventList.count({where: publicListsWhere}),
          db.eventList.findMany({
            where: publicListsWhere,
            orderBy: {createdAt: 'desc'},
            take: PUBLIC_LIST_PREVIEW_COUNT,
            include: {_count: {select: {items: true, followers: true, subscriptions: true}}},
          }),
          db.profileFollower.count({where: {followedProfileId: profile.id}}),
          db.profileFollower.count({where: {followerProfileId: profile.id}}),
        ]);

      const publicLists = listRows.map(toEventListSummary);

      const isOwner =
        viewerProfileId !== null && viewerProfileId === profile.id;
      const isFollowing =
        viewerProfileId !== null && !isOwner
          ? Boolean(
              await db.profileFollower.findUnique({
                where: {
                  followerProfileId_followedProfileId: {
                    followerProfileId: viewerProfileId,
                    followedProfileId: profile.id,
                  },
                },
                select: {id: true},
              })
            )
          : false;

      return {
        profile,
        publicListsCount,
        publicLists,
        social: {followerCount, followingCount, isOwner, isFollowing},
        memberships,
      };
    }
  );

const GetPublicProfileListsInput = z.object({
  profileId: z.string().uuid(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(50).default(12),
});

/**
 * Returns a paginated page of a profile's PUBLIC event lists, ordered
 * most-recent first. Returns null when the profile id does not exist.
 * Used by the public all-lists page at /rider/$profileId/lists.
 */
export const getPublicProfileLists = createServerFn({method: 'GET'})
  .inputValidator((data: unknown) => {
    const result = GetPublicProfileListsInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data}): Promise<PublicProfileListsPage | null> => {
    const db = await prismaClient();

    const profile = await db.profile.findUnique({
      where: {id: data.profileId},
      select: {
        id: true,
        displayName: true,
        firstName: true,
        lastName: true,
        avatarUrl: true,
      },
    });

    if (!profile) return null;

    const where = {profileId: profile.id, visibility: 'PUBLIC'};
    const skip = (data.page - 1) * data.pageSize;

    const [totalCount, listRows] = await db.$transaction([
      db.eventList.count({where}),
      db.eventList.findMany({
        where,
        orderBy: {createdAt: 'desc'},
        skip,
        take: data.pageSize,
        include: {_count: {select: {items: true, followers: true, subscriptions: true}}},
      }),
    ]);

    const totalPages = Math.max(1, Math.ceil(totalCount / data.pageSize));

    return {
      profile,
      lists: listRows.map(toEventListSummary),
      pagination: {
        page: data.page,
        pageSize: data.pageSize,
        totalCount,
        totalPages,
        hasNextPage: data.page < totalPages,
        hasPreviousPage: data.page > 1,
      },
    };
  });

const GetPublicProfileEventsInput = z.object({
  profileId: z.string().uuid(),
});

/**
 * Returns upcoming events the given profile is tracking (attending) or
 * registered for, deduped, sorted by start date ascending. Public — no
 * auth required. Returns [] when the profile id has no matching events.
 */
export const getPublicProfileEvents = createServerFn({method: 'GET'})
  .inputValidator((data: unknown) => {
    const result = GetPublicProfileEventsInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data}): Promise<Event[]> => {
    const db = await prismaClient();
    const now = new Date();

    const [attendances, registrations] = await db.$transaction([
      db.eventAttendance.findMany({
        where: {profileId: data.profileId, event: futureEventFilter(now)},
        include: {event: {include: eventCardInclude}},
      }),
      db.registration.findMany({
        where: {
          profileId: data.profileId,
          status: {in: ['APPROVED', 'PAID']},
          event: futureEventFilter(now),
        },
        include: {event: {include: eventCardInclude}},
      }),
    ]);

    const byId = new Map<string, (typeof attendances)[number]['event']>();
    for (const a of attendances) byId.set(a.event.id, a.event);
    for (const r of registrations) byId.set(r.event.id, r.event);

    const events = z.array(Event).parse(Array.from(byId.values()));

    events.sort((a, b) => {
      if (a.startDate === null && b.startDate === null) return 0;
      if (a.startDate === null) return 1;
      if (b.startDate === null) return -1;
      return a.startDate.getTime() - b.startDate.getTime();
    });

    return events;
  });
